import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useConfirm } from '../ui/ConfirmDialog';

const TeamMembersSettings = ({ team, setTeam, refreshTeamData }) => {
  const confirm = useConfirm();
  const [inviting, setInviting] = useState(false);
  const [invitation, setInvitation] = useState({
    email: '',
    role: 'team_member'
  });
  const [invitationLink, setInvitationLink] = useState('');
  const [showInvitationLink, setShowInvitationLink] = useState(false);
  const linkRef = useRef(null);
  
  // State for delete confirmation dialog
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [confirmationName, setConfirmationName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleInvitationChange = (e) => {
    const { name, value } = e.target;
    setInvitation(prev => ({ ...prev, [name]: value }));
  };

  const sendInvitation = async (e) => {
    e.preventDefault();
    
    if (!invitation.email.trim()) {
      toast.error('Email is required');
      return;
    }
    
    setInviting(true);
    
    try {
      const response = await axios.post('/api/teams/invite', {
        email: invitation.email,
        role: invitation.role
      });
      
      // Get the invitation link from the response
      if (response.data.invitationLink) {
        setInvitationLink(response.data.invitationLink);
        setShowInvitationLink(true);
        toast.success(`Email sent to ${invitation.email}. You can also copy the invitation link below.`);
        
        // Don't refresh the page when showing an invitation link
        // This gives the user time to copy the link
      } else {
        toast.success(`Invitation sent to ${invitation.email}`);
        // Reset form
        setInvitation({
          email: '',
          role: 'team_member'
        });
        setShowInvitationLink(false);
        
        // Refresh team data to show updated members list
        if (refreshTeamData) {
          refreshTeamData();
        }
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error(error.response?.data?.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };
  
  const copyToClipboard = () => {
    if (linkRef.current) {
      linkRef.current.select();
      document.execCommand('copy');
      toast.success('Invitation link copied to clipboard!');
    }
  };

  // Open the delete confirmation dialog
  const openDeleteDialog = (member) => {
    setMemberToDelete(member);
    setConfirmationName('');
    setIsDeleteDialogOpen(true);
  };
  
  // Close the delete confirmation dialog
  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setMemberToDelete(null);
    setConfirmationName('');
  };
  
  // Handle confirmation name input change
  const handleConfirmationNameChange = (e) => {
    setConfirmationName(e.target.value);
  };
  
  // Check if confirmation name matches the member's full name
  const isConfirmationValid = () => {
    if (!memberToDelete) return false;
    const fullName = `${memberToDelete.firstName} ${memberToDelete.lastName}`;
    return confirmationName.toLowerCase() === fullName.toLowerCase();
  };
  
  // Remove team member after confirmation
  const removeTeamMember = async () => {
    if (!memberToDelete || !isConfirmationValid()) return;
    
    try {
      setIsDeleting(true);
      await axios.delete(`/api/teams/members/${memberToDelete.id}`);
      
      // Update the local state - either remove the member or mark as inactive
      const updatedMembers = team.members.map(member => 
        member.id === memberToDelete.id ? { ...member, isActive: false } : member
      );
      
      setTeam(prev => ({ ...prev, members: updatedMembers }));
      
      toast.success(`${memberToDelete.firstName} ${memberToDelete.lastName} has been removed from the team`);
      closeDeleteDialog();
    } catch (error) {
      console.error('Error removing team member:', error);
      toast.error(error.response?.data?.message || 'Failed to remove team member');
    } finally {
      setIsDeleting(false);
    }
  };

  const updateMemberRole = async (memberId, newRole) => {
    try {
      await axios.put(`/api/teams/members/${memberId}/role`, { role: newRole });
      
      const updatedMembers = team.members.map(member => 
        member.id === memberId ? { ...member, role: newRole } : member
      );
      
      setTeam(prev => ({ ...prev, members: updatedMembers }));
      
      toast.success('Member role updated');
    } catch (error) {
      console.error('Error updating member role:', error);
      toast.error(error.response?.data?.message || 'Failed to update member role');
    }
  };

  // Function to handle canceling an invitation
  const handleCancelInvite = async (invitationId) => {
    const ok = await confirm({
      title: 'Cancel this invitation?',
      description: 'The invitation link will stop working and the licence will be freed up.',
      confirmLabel: 'Cancel invitation',
      cancelLabel: 'Keep it',
      destructive: true
    });
    if (!ok) {
      return;
    }
    
    try {
      await axios.delete(`/api/invitations/${invitationId}`);
      toast.success('Invitation cancelled successfully');
      
      // Refresh team data to update the UI
      if (refreshTeamData) {
        refreshTeamData();
      }
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      toast.error('Failed to cancel invitation');
    }
  };
  
  // Function to handle resending an invitation
  const handleResendInvite = async (invitationId) => {
    try {
      await axios.post(`/api/invitations/resend/${invitationId}`);
      toast.success('Invitation resent successfully');
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast.error('Failed to resend invitation');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
      <div className="p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Team Members</h2>
        
        <div className="space-y-6">
          {/* Pending Invitations Section */}
          <div className="mb-8">
            <h3 className="text-md font-medium text-gray-900 dark:text-white mb-2">Pending Invitations</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              These invitations are using licenses. Cancel any unneeded invitations to free up licenses.
            </p>
            
            {team.pendingInvitations && team.pendingInvitations.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Email
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Sent Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {team.pendingInvitations.map((invitation) => (
                      <tr key={invitation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {invitation.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                          {invitation.createdAt ? new Date(invitation.createdAt).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleResendInvite(invitation.id)}
                              className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                              title="Resend invitation email"
                            >
                              Resend
                            </button>
                            <button
                              onClick={() => handleCancelInvite(invitation.id)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              title="Cancel invitation and free up license"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">No pending invitations.</p>
            )}
          </div>
          
          {/* Current team members */}
          {team.members.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Role
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Joined Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {team.members.map((member) => (
                    <tr key={member.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {member.avatar ? (
                              <img className="h-10 w-10 rounded-full" src={member.avatar} alt="" />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                                <span className="text-gray-500 dark:text-gray-300 font-medium">
                                  {member.firstName ? member.firstName.charAt(0).toUpperCase() : '?'}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : member.email.split('@')[0]}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {member.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={member.role}
                          onChange={(e) => updateMemberRole(member.id, e.target.value)}
                          className="block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                          <option value="team_member">Team Member</option>
                          <option value="team_admin">Team Admin</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          member.isActive 
                            ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' 
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                        }`}>
                          {member.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {member.createdAt ? new Date(member.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {/* Only show delete button for active users */}
                        {member.isActive && (
                          <button
                            type="button"
                            onClick={() => openDeleteDialog(member)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No team members yet. Invite your first team member below.</p>
          )}
          
          {/* Invite new member form */}
          <div className="card bg-white dark:bg-gray-800 shadow overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Invite Team Member</h3>
              <div className="mt-5">
                <form onSubmit={sendInvitation} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={invitation.email}
                      onChange={handleInvitationChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="colleague@example.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                    <select
                      id="role"
                      name="role"
                      value={invitation.role}
                      onChange={handleInvitationChange}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="team_member">Team Member</option>
                      <option value="team_admin">Team Admin</option>
                    </select>
                  </div>
                  
                  {showInvitationLink && (
                    <div className="mt-4">
                      <label htmlFor="invitationLink" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Invitation Link (Copy to share)
                      </label>
                      <div className="flex">
                        <input
                          type="text"
                          id="invitationLink"
                          ref={linkRef}
                          value={invitationLink}
                          readOnly
                          className="block w-full border-gray-300 rounded-l-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={copyToClipboard}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-r-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          Copy
                        </button>
                      </div>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        Share this link with your team member to join your team.
                      </p>
                    </div>
                  )}
                  
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={inviting}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    >
                      {inviting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : 'Generate Invitation'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Delete confirmation dialog */}
      <Transition appear show={isDeleteDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => !isDeleting && closeDeleteDialog()}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    Remove Team Member
                  </Dialog.Title>
                  
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to remove <span className="font-medium">{memberToDelete ? `${memberToDelete.firstName} ${memberToDelete.lastName}` : ''}</span> from your team? 
                      <span className="font-bold text-red-600 dark:text-red-400">THIS CANNOT BE UNDONE.</span>
                    </p>
                    
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      To confirm, please type the full name of the team member: <span className="font-medium">{memberToDelete ? `${memberToDelete.firstName} ${memberToDelete.lastName}` : ''}</span>
                    </p>
                    
                    <div className="mt-3">
                      <input
                        type="text"
                        value={confirmationName}
                        onChange={handleConfirmationNameChange}
                        placeholder="Type full name to confirm"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        disabled={isDeleting}
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-gray-200 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                      onClick={closeDeleteDialog}
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${isConfirmationValid() ? 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500' : 'bg-red-300 cursor-not-allowed'}`}
                      onClick={removeTeamMember}
                      disabled={!isConfirmationValid() || isDeleting}
                    >
                      {isDeleting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Removing...
                        </>
                      ) : 'Remove Team Member'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default TeamMembersSettings;
